/**
 * useGpxAnalysis Hook
 *
 * Custom hook for uploading and analyzing GPX files with category-specific intelligence.
 * Integrates with backend GPX Intelligence Service.
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-237 (US-1.20.6-S.3-02: GPX Analysis Hook)
 *
 * @module useGpxAnalysis
 */

import { useState, useCallback } from 'react';
import apiRequest from '../../services/api';

/**
 * useGpxAnalysis
 *
 * Handles GPX file upload and analysis with loading/error states.
 *
 * @param {Object} options - Hook configuration
 * @param {string} options.experienceId - Experience ID (or 'new' for standalone)
 * @param {number} options.categoryL1Id - L1 Category ID (optional, used for strategy selection)
 * @returns {Object} Hook state and methods
 */
const useGpxAnalysis = ({ experienceId, categoryL1Id }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [gpxRawXml, setGpxRawXml] = useState(null);

  /**
   * Analyze GPX file
   *
   * Uploads file to backend and receives category-specific analysis.
   *
   * @param {File} file - GPX file to analyze
   * @returns {Promise<Object>} Analysis result
   */
  const analyze = useCallback(
    async (file) => {
      // Validate file
      if (!file) {
        setError('No file provided');
        return null;
      }

      if (!file.name.toLowerCase().endsWith('.gpx')) {
        setError('Only GPX files are supported');
        return null;
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        setError('File size must be less than 10MB');
        return null;
      }

      setIsAnalyzing(true);
      setError(null);
      setUploadProgress(0);
      setAnalysisResult(null);
      setGpxRawXml(null);

      try {
        // SCRUM-830: Read GPX file as text for later persistence
        const rawXml = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read GPX file'));
          reader.readAsText(file);
        });

        // Create form data
        const formData = new FormData();
        formData.append('file', file);

        if (categoryL1Id) {
          formData.append('categoryL1Id', categoryL1Id.toString());
        }

        const result = await apiRequest(
          `/api/v1/experiences/${experienceId}/routes/gpx/analyze`,
          {
            method: 'POST',
            body: formData,
          }
        );

        if (!result.success) {
          throw new Error(result.message || 'Analysis failed');
        }

        setAnalysisResult(result.data);
        setGpxRawXml(rawXml);
        setUploadProgress(100);
        setIsAnalyzing(false);

        // SCRUM-830: Include raw GPX XML for route persistence
        return { ...result.data, gpxRawXml: rawXml, gpxFilename: file.name };
      } catch (err) {
        console.error('[useGpxAnalysis] Analysis error:', err);
        setError(err.message || 'Failed to analyze GPX file');
        setIsAnalyzing(false);
        setUploadProgress(0);
        return null;
      }
    },
    [experienceId, categoryL1Id]
  );

  /**
   * Reset analysis state
   */
  const reset = useCallback(() => {
    setIsAnalyzing(false);
    setAnalysisResult(null);
    setError(null);
    setUploadProgress(0);
    setGpxRawXml(null);
  }, []);

  return {
    // State
    isAnalyzing,
    analysisResult,
    error,
    uploadProgress,
    gpxRawXml,

    // Methods
    analyze,
    reset
  };
};

export default useGpxAnalysis;
